import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface NewsItem {
  id: number;
  title: string;
  summary: string;
  publishedAt: string;
  category: {
    name: string;
  };
  imageUrl?: string;
}

const NewsSection = () => {
  const [newsData, setNewsData] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sectionTitle, setSectionTitle] = useState("最新动态");
  const [sectionDescription, setSectionDescription] = useState("了解画玺科技在超黑材料领域的最新突破与行业动态");

  useEffect(() => {
    const fetchLatestNews = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/news?page=1&limit=4&excludeCategoryId=33');
        
        if (!response.ok) {
          throw new Error('获取新闻失败');
        }
        
        const data = await response.json();
        
        if (data.news && data.news.length > 0) {
          setNewsData(data.news);
        } else {
          // 如果没有新闻数据，使用默认占位数据
          setNewsData([
            {
              id: 1,
              title: "太阳黑洞6.0技术突破：光吸收率达到99.8%",
              summary: "我们的研发团队在超黑材料领域取得重大突破，新一代太阳黑洞6.0涂层的光吸收率提升至99.8%，为航空航天领域带来革命性解决方案。",
              publishedAt: "2024-01-15T00:00:00.000Z",
              category: { name: "技术突破" },
              imageUrl: "/api/placeholder/400/250"
            },
            {
              id: 2,
              title: "画玺科技与NASA达成战略合作",
              summary: "我们的超黑涂层技术获得NASA认可，将为下一代太空望远镜提供关键的光学组件黑化解决方案，助力深空探索任务。",
              publishedAt: "2024-01-10T00:00:00.000Z",
              category: { name: "合作伙伴" },
              imageUrl: "/api/placeholder/400/250"
            }
          ]);
        }
      } catch (err) {
        console.error('获取新闻数据失败:', err);
        setError('获取新闻数据失败，请稍后重试');
        
        // 出错时使用默认数据
        setNewsData([
          {
            id: 1,
            title: "太阳黑洞6.0技术突破：光吸收率达到99.8%",
            summary: "我们的研发团队在超黑材料领域取得重大突破，新一代太阳黑洞6.0涂层的光吸收率提升至99.8%，为航空航天领域带来革命性解决方案。",
            publishedAt: "2024-01-15T00:00:00.000Z",
            category: { name: "技术突破" },
            imageUrl: "/api/placeholder/400/250"
          },
          {
            id: 2,
            title: "画玺科技与NASA达成战略合作",
            summary: "我们的超黑涂层技术获得NASA认可，将为下一代太空望远镜提供关键的光学组件黑化解决方案，助力深空探索任务。",
            publishedAt: "2024-01-10T00:00:00.000Z",
            category: { name: "合作伙伴" },
            imageUrl: "/api/placeholder/400/250"
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestNews();
  }, []);

  // 获取首页设置
  useEffect(() => {
    const fetchHomepageSettings = async () => {
      try {
        const response = await fetch('/api/homepage/settings');
        const result = await response.json();
        
        if (result.success) {
          if (result.data.section7Title) {
            setSectionTitle(result.data.section7Title);
          }
          if (result.data.section7Introduction) {
            setSectionDescription(result.data.section7Introduction);
          }
        }
      } catch (err) {
        console.error('获取首页设置失败:', err);
      }
    };

    fetchHomepageSettings();
  }, []);

  // 格式化日期
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  };

  // 分类颜色映射
  const categoryColors: Record<string, string> = {
    "技术突破": "bg-blue-100 text-blue-800",
    "合作伙伴": "bg-green-100 text-green-800",
    "应用案例": "bg-purple-100 text-purple-800",
    "荣誉奖项": "bg-yellow-100 text-yellow-800",
    "公司动态": "bg-red-100 text-red-800",
    "科研应用": "bg-indigo-100 text-indigo-800"
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-orange-500/10 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-red-600/10 via-transparent to-transparent"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                {sectionTitle}
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                {sectionDescription}
              </p>
            </motion.div>
          </div>
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            <p className="mt-4 text-gray-400">加载中...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-orange-500/10 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-red-600/10 via-transparent to-transparent"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {sectionTitle}
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {sectionDescription}
            </p>
            </motion.div>
          </div>
          <div className="text-center text-red-400">
            <p>{error}</p>
          </div>
        </div>
      </section>
    );
  }

  const displayedNews = newsData.slice(0, 4);

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-orange-500/10 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-red-600/10 via-transparent to-transparent"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {sectionTitle}
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {sectionDescription}
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {displayedNews.map((news, index) => (
            <motion.a
              key={news.id}
              href={`/news/${news.id}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="block cursor-pointer group"
            >
              <div className="bg-black/40 backdrop-blur-lg rounded-3xl overflow-hidden border border-white/10 hover:border-orange-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-orange-500/20 h-full">
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={news.imageUrl || "/api/placeholder/400/250"}
                    alt={news.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute top-4 right-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${categoryColors[news.category.name as keyof typeof categoryColors]} text-white backdrop-blur-sm`}>
                      {news.category.name}
                    </span>
                  </div>
                </div>

                <div className="p-8">
                   <h3 className="text-xl md:text-2xl font-bold text-white mb-4 leading-tight group-hover:text-orange-400 transition-colors duration-300">
                     {news.title}
                   </h3>

                   <p className="text-gray-300 text-base leading-relaxed mb-6 line-clamp-2">
                     {news.summary}
                   </p>

                   <div className="flex justify-between items-center">
                     <div className="flex items-center text-orange-500 font-medium group-hover:text-orange-400 transition-colors duration-300">
                       <span>阅读详情</span>
                       <svg className="ml-2 w-4 h-4 transform group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                       </svg>
                     </div>
                     <span className="text-orange-400 text-sm font-medium">
                       {formatDate(news.publishedAt)}
                     </span>
                   </div>
                 </div>
              </div>
            </motion.a>
          ))}
        </div>

        <div className="text-center">
          <motion.a
            href="/news"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white font-medium rounded-xl hover:from-orange-600 hover:to-red-700 transition-all duration-300 shadow-lg hover:shadow-orange-500/25"
          >
            查看所有新闻
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;